import {ActionList, AnchoredOverlay, asSlot, Spinner} from '@primer/react'
import {SessionLoadingIcon} from '@github-ui/agent-sessions/components/StatusIcon'
import {AlertIcon, CheckIcon, DotFillIcon, EyeIcon, InfoIcon, QuestionBubbleIcon, XIcon} from '@primer/octicons-react'
import {AgentMergePresentation_Kind as Kind} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'
import {useAgentMergeAssigneeSession} from '@github-ui/copilot_agent_merge/hooks/use-agent-merge-assignee-session'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useMergeBoxPageData, useMergeMethodContext} from '@github-ui/mergebox'
import {useEffect, useRef, useState, type ReactNode, type Ref} from 'react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {ownerPath} from '@github-ui/paths'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {
  AssigneesResponse as AssigneesData,
  Assignee,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/assignees'
import styles from './Assignees.module.css'
import sidebarListStyles from './SidebarActionList.module.css'
import {UserSectionSkeleton} from './UserSectionSkeleton'
import {useAssignees} from './data/use-assignees'
import {useOverviewRouteData} from './data/use-overview-route-data'
import {AssignYourself} from './AssignYourself'
import {useLayoutQuery} from '../../page-data/loaders/use-layout-query'
import {useIsFeatureStateEnabled} from '../../hooks/use-feature-state'

export function Assignees() {
  const {data: assigneesData, isPending, isError} = useAssignees()
  const {user} = useOverviewRouteData().data

  if (isPending && !assigneesData) return <UserSectionSkeleton placeholderRows={1} />

  if (isError && !assigneesData) {
    return <span className={sidebarListStyles.emptyState}>Unable to load assignees</span>
  }

  return <AssigneesComponent assigneesData={assigneesData} canAssign={user?.canAssign ?? false} />
}

function AssigneesComponent({assigneesData, canAssign}: {assigneesData: AssigneesData; canAssign: boolean}) {
  const assignees = [...(assigneesData.assignees ?? [])].sort((a, b) =>
    a.displayLogin.localeCompare(b.displayLogin, undefined, {sensitivity: 'base'}),
  )

  if (assignees.length === 0) {
    return <span className={sidebarListStyles.emptyState}>{canAssign ? <AssignYourself /> : 'No one assigned'}</span>
  }

  return (
    <ActionList variant="full" className={sidebarListStyles.flush}>
      {assignees.map(assignee =>
        isCopilot(assignee.displayLogin) ? (
          <CopilotAssigneeRow key={assignee.id} assignee={assignee} />
        ) : (
          <AssigneeRow key={assignee.id} assignee={assignee} />
        ),
      )}
    </ActionList>
  )
}

const COPILOT_DISPLAY_NAME = 'Copilot'
const WATCHING_LABEL = 'Copilot is watching this pull request'
const CHECKING_LABEL = 'Checking Copilot status'

function CriticalStatusIcon() {
  return <XIcon className={styles.StatusCritical} />
}

function WarningStatusIcon() {
  return <AlertIcon className={styles.StatusWarning} />
}

function SuccessStatusIcon() {
  return <CheckIcon className={styles.StatusSuccess} />
}

function resolvedStatusIcon(kind?: Kind) {
  switch (kind) {
    case Kind.BUSY:
      return SessionLoadingIcon
    case Kind.CRITICAL:
      return CriticalStatusIcon
    case Kind.WARNING:
      return WarningStatusIcon
    case Kind.SUCCESS:
      return SuccessStatusIcon
    default:
      return InfoIcon
  }
}

const CopilotStatusInfo = asSlot(function CopilotStatusInfoImpl({
  pending,
  message,
  kind,
  onFocus,
  onBlur,
}: {
  pending: boolean
  message?: string
  kind?: Kind
  onFocus: () => void
  onBlur: () => void
}) {
  const [open, setOpen] = useState(false)
  const label = pending ? CHECKING_LABEL : (message ?? WATCHING_LABEL)

  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      width="small"
      align="end"
      displayCloseButton
      overlayProps={{
        role: 'dialog',
        'aria-label': label,
        'aria-modal': true,
        onFocusCapture: onFocus,
        onBlurCapture: onBlur,
      }}
      renderAnchor={props => (
        <ActionList.TrailingAction
          {...props}
          icon={pending ? SessionLoadingIcon : message ? resolvedStatusIcon(kind) : EyeIcon}
          label={label}
          tooltipDirection="n"
          className={styles.SessionIndicator}
          onFocusCapture={onFocus}
          onBlurCapture={onBlur}
        />
      )}
    >
      <p className={styles.StatusDetails}>
        {pending
          ? 'Checking whether Copilot is ready to watch or has an active session.'
          : (message ?? 'Copilot is watching this pull request and will start a session when there is work to do.')}
      </p>
    </AnchoredOverlay>
  )
}, ActionList.TrailingAction)

function SessionSpinner() {
  return <Spinner size="small" srText={null} className={styles.SessionSpinner} />
}

const sessionStatus = {
  running: {label: 'Working', icon: SessionLoadingIcon},
  starting: {label: 'Starting', icon: SessionSpinner},
  waiting_for_user: {label: 'Needs your input', icon: QuestionBubbleIcon},
  idle: {label: 'Waiting', icon: DotFillIcon},
}

function CopilotAssigneeRow({assignee}: {assignee: Assignee}) {
  const enabled = useIsFeatureStateEnabled('agentMerge')
  return enabled && isFeatureEnabled('prs_copilot_assignee_session_indicator') ? (
    <ActiveCopilotAssigneeRow assignee={assignee} />
  ) : (
    <AssigneeRow assignee={assignee} />
  )
}

function ActiveCopilotAssigneeRow({assignee}: {assignee: Assignee}) {
  const identityRef = useRef<HTMLAnchorElement>(null)
  const sessionFocused = useRef(false)
  const infoFocused = useRef(false)
  const {aliveChannels, mergeStatusButtonData} = useLayoutQuery().data
  const {mergeMethod} = useMergeMethodContext()
  const mergeBoxQuery = useMergeBoxPageData({mergeMethod})
  const {data: mergeBox, isError: mergeBoxError} = mergeBoxQuery
  const currentHeadSha = mergeBox?.pullRequest.headRefOid ?? ''
  const {session, message, kind, isError, isWatching, isPending} = useAgentMergeAssigneeSession({
    enabled: Boolean(currentHeadSha),
    currentHeadSha,
    pullRequestChannel: aliveChannels?.pullRequest,
    viewerLogin: mergeStatusButtonData?.viewerLogin,
  })
  const status = session && sessionStatus[session.status]
  const checkingStatus = isPending || mergeBoxQuery.isPending || (mergeBoxQuery.isFetching && mergeBoxError)
  const showStatusInfo = !session && (Boolean(message) || isWatching || checkingStatus)
  useEffect(() => {
    if ((!session && sessionFocused.current) || (!showStatusInfo && infoFocused.current)) {
      sessionFocused.current = false
      infoFocused.current = false
      identityRef.current?.focus()
    }
  }, [session, showStatusInfo])

  return (
    <AssigneeRow
      assignee={assignee}
      identityRef={identityRef}
      sessionError={!checkingStatus && (isError || mergeBoxError)}
      showStatusInfo={showStatusInfo}
      statusMessage={message}
      statusKind={kind}
      checkingStatus={checkingStatus}
      onInfoFocus={() => {
        infoFocused.current = true
      }}
      onInfoBlur={() => {
        infoFocused.current = false
      }}
      trailingAction={
        !checkingStatus && session && status ? (
          <ActionList.TrailingAction
            as="a"
            href={session.url}
            icon={kind === undefined ? status.icon : resolvedStatusIcon(kind)}
            label={
              session.description ?? `View Copilot session: ${status.label}${session.goal ? ` — ${session.goal}` : ''}`
            }
            className={styles.SessionIndicator}
            tooltipDirection="n"
            onFocus={() => {
              sessionFocused.current = true
            }}
            onBlur={() => {
              sessionFocused.current = false
            }}
          />
        ) : null
      }
    />
  )
}

function AssigneeRow({
  assignee,
  trailingAction,
  sessionError,
  identityRef,
  showStatusInfo,
  statusMessage,
  statusKind,
  checkingStatus,
  onInfoFocus,
  onInfoBlur,
}: {
  assignee: Assignee
  trailingAction?: ReactNode
  sessionError?: boolean
  identityRef?: Ref<HTMLAnchorElement>
  showStatusInfo?: boolean
  statusMessage?: string
  statusKind?: Kind
  checkingStatus?: boolean
  onInfoFocus?: () => void
  onInfoBlur?: () => void
}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const copilot = isCopilot(assignee.displayLogin)
  const appBacked = copilot || Boolean(assignee.slug)

  const displayName = copilot ? COPILOT_DISPLAY_NAME : assignee.displayLogin
  const appSlug = assignee.slug || assignee.displayLogin
  const href = appBacked ? `/apps/${appSlug}` : ownerPath({owner: assignee.displayLogin})
  const hovercardAttrs = appBacked
    ? getHovercardAttributesForType('copilot', {botSlug: appSlug})
    : getHovercardAttributesForType('user', {login: assignee.displayLogin})

  return (
    <ActionList.LinkItem ref={identityRef} href={href} className={styles.assigneeItem} {...hovercardAttrs}>
      <ActionList.LeadingVisual>
        {copilot ? <CopilotAvatar isDecorative /> : <GitHubAvatar src={assignee.avatarUrl} size={20} />}
      </ActionList.LeadingVisual>
      <span className={styles.assigneeName}>{displayName}</span>
      {sessionError && <ActionList.Description variant="block">Unable to load Copilot session</ActionList.Description>}
      {trailingAction}
      {showStatusInfo && onInfoFocus && onInfoBlur && (
        <CopilotStatusInfo
          pending={Boolean(checkingStatus)}
          message={statusMessage}
          kind={statusKind}
          onFocus={onInfoFocus}
          onBlur={onInfoBlur}
        />
      )}
      {sessionError && (
        <ActionList.TrailingVisual>
          <AlertIcon aria-hidden="true" />
        </ActionList.TrailingVisual>
      )}
    </ActionList.LinkItem>
  )
}
